// WeatherState — single source of truth for weather driven by biome data.
// Modes: clear | overcast | rain | snow
// Reads biome result → produces cloudCover01, precip01, temperature01, wind.

const BIOME_CLIMATE = {
  ocean:      { temp01: 0.55, humidity01: 0.85 },
  grass:      { temp01: 0.60, humidity01: 0.60 },
  grassland:  { temp01: 0.65, humidity01: 0.55 },
  mountain:   { temp01: 0.40, humidity01: 0.45 },
  snow:       { temp01: 0.15, humidity01: 0.35 },
  desert:     { temp01: 0.90, humidity01: 0.15 },
  deadplains: { temp01: 0.70, humidity01: 0.20 },
};

const HOLD_MIN_MS = 90_000;
const HOLD_MAX_MS = 180_000;
const MOOD_BUCKET_MS = 120_000;
const TEMP_SNOW_CUTOFF = 0.35;
const CLOUD_COVER_MIN = 0.05;
const CLOUD_COVER_MAX = 0.55;
const PRECIP_MAX = 0.45;
const WIND_DIR = { x: 0.906, y: 0.423 }; // normalized (0.75, 0.35)
const WIND_SPEED = 6;
const STORAGE_KEY = 'hexaworlds_weather';

function hash01(seed, bucket) {
  let h = (seed * 2654435761 + bucket * 2246822519) | 0;
  h = ((h >>> 16) ^ h) * 0x45d9f3b | 0;
  h = ((h >>> 16) ^ h) * 0x45d9f3b | 0;
  h = (h >>> 16) ^ h;
  return (h & 0x7fffffff) / 0x7fffffff;
}

function lerpExp(curr, target, timeConstSec, dtSec) {
  const a = 1 - Math.exp(-dtSec / timeConstSec);
  return curr + (target - curr) * a;
}

function smoothstep(edge0, edge1, x) {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

export class WeatherState {
  constructor(worldSeed) {
    this.worldSeed = worldSeed;
    this.mode = 'clear';
    this.targetMode = 'clear';
    this.cloudCover01 = 0.1;
    this.precip01 = 0;
    this.temperature01 = 0.5;
    this.wind = { x: WIND_DIR.x * WIND_SPEED, y: WIND_DIR.y * WIND_SPEED };
    this.modeHoldUntilMs = 0;
    this.enabled = true;
    this._debugForce = null; // 'rain' | 'snow' | null
    this._loadSettings();
  }

  /** Force rain or snow for testing. Pass null to return to normal. */
  forceMode(mode) {
    this._debugForce = mode;
    if (mode === 'rain' || mode === 'snow') {
      this.enabled = true;
      this.mode = mode;
      this.targetMode = mode;
      this.precip01 = PRECIP_MAX;
      this.cloudCover01 = CLOUD_COVER_MAX;
    } else {
      this.mode = 'clear';
      this.targetMode = 'clear';
      this.precip01 = 0;
      this.cloudCover01 = 0.1;
    }
  }

  /** Called every frame. dtMs in milliseconds. */
  update(dtMs, nowMs, biomeResult) {
    if (!this.enabled) return;
    if (this._debugForce) return; // skip natural weather when forced
    const dtSec = Math.min(dtMs / 1000, 0.1);
    if (dtSec <= 0) return;

    // Resolve biome climate
    const biome = biomeResult && biomeResult.targetBiome;
    const climate = (biome && BIOME_CLIMATE[biome]) || { temp01: 0.5, humidity01: 0.3 };

    // World mood noise — slow drift
    const bucket = Math.floor(nowMs / MOOD_BUCKET_MS);
    const noise01 = hash01(this.worldSeed, bucket);

    // Cloud cover target
    const cloudRaw = clamp01(climate.humidity01 * 0.55 + noise01 * 0.25);
    const cloudTarget = CLOUD_COVER_MIN + cloudRaw * (CLOUD_COVER_MAX - CLOUD_COVER_MIN);

    // Precip target — only when cloud cover is high enough
    const precipRaw = smoothstep(0.45, 0.75, cloudRaw) * climate.humidity01;
    const precipTarget = Math.min(precipRaw, PRECIP_MAX);

    // Temperature target
    const tempTarget = climate.temp01;

    // Mode selection (only change when hold timer expires)
    let newMode;
    if (precipTarget < 0.08) {
      newMode = cloudTarget > 0.30 ? 'overcast' : 'clear';
    } else {
      newMode = tempTarget < TEMP_SNOW_CUTOFF ? 'snow' : 'rain';
    }

    if (newMode !== this.targetMode && nowMs >= this.modeHoldUntilMs) {
      this.targetMode = newMode;
      this.mode = newMode;
      this.modeHoldUntilMs = nowMs + HOLD_MIN_MS + Math.random() * (HOLD_MAX_MS - HOLD_MIN_MS);
    }

    // Exponential smoothing
    this.cloudCover01 = lerpExp(this.cloudCover01, cloudTarget, 3.0, dtSec);
    this.precip01 = lerpExp(this.precip01, precipTarget, 3.0, dtSec);
    this.temperature01 = lerpExp(this.temperature01, tempTarget, 8.0, dtSec);

    // Wind — slight variation from mood noise
    const windSpeed = WIND_SPEED * (0.8 + noise01 * 0.4);
    this.wind.x = WIND_DIR.x * windSpeed;
    this.wind.y = WIND_DIR.y * windSpeed;
  }

  setEnabled(on) {
    this.enabled = on;
    if (!on) {
      this.cloudCover01 = 0.1;
      this.precip01 = 0;
      this.mode = 'clear';
      this.targetMode = 'clear';
    }
    this._saveSettings();
  }

  _loadSettings() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (typeof s.enabled === 'boolean') this.enabled = s.enabled;
      }
    } catch (_) { /* ignore */ }
  }

  _saveSettings() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ enabled: this.enabled }));
    } catch (_) { /* ignore */ }
  }
}
